import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi } from '@/lib/pillarApi';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    PasswordPolicyFeedback,
    passwordPolicyBlocksSubmit,
    usePasswordPolicyFeedback,
} from '@/components/auth/PasswordPolicyFeedback';
import {
    SessionUserProfileDetailedResponse,
    SessionUserProfileDetailedResponseSchema,
} from '@pillar-portal/api-types';
import {
    hasNativeAppLoginContinuation,
    NATIVE_APP_LOGIN_PATH,
} from '@/lib/native-app-login-continuation';

function PasswordContent() {
    const log = createComponentLogger('ProfilePasswordPage');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const postPasswordChangePath = hasNativeAppLoginContinuation(searchParams)
        ? NATIVE_APP_LOGIN_PATH
        : '/dashboard';
    const queryClient = useQueryClient();
    const setUser = useAuthStore((state) => state.setUser);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState<string | null>(null);
    const passwordPolicyState = usePasswordPolicyFeedback(newPassword);

    const changePasswordMutation = useMutation({
        mutationFn: authApi.changePassword,
        onSuccess: async () => {
            const refreshedProfileResponse = await sessionSensitiveAuthApi.getProfile();
            const refreshedProfile: SessionUserProfileDetailedResponse =
                SessionUserProfileDetailedResponseSchema.parse(refreshedProfileResponse);
            setUser(refreshedProfile);
            queryClient.setQueryData(['profile'], refreshedProfile);
            await queryClient.invalidateQueries({ queryKey: ['activeRoleAssignment'] });
            await queryClient.invalidateQueries({ queryKey: ['impersonationContext'] });
            await queryClient.invalidateQueries({ queryKey: ['pendingRequiredLegalDocuments'] });

            setError(null);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            runInBackground(navigate(postPasswordChangePath, { replace: true }), (err) => {
                log.error('Error navigating after password change', err);
            });
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage('');

        if (newPassword !== confirmPassword) {
            setError('New password and confirmation do not match');
            return;
        }

        if (passwordPolicyBlocksSubmit(passwordPolicyState, newPassword)) {
            setError('New password does not satisfy the password policy');
            return;
        }

        changePasswordMutation.mutate({
            old_password: currentPassword,
            new_password: newPassword,
        });
    };

    return (
        <div>
            <BackButton href="/profile" tooltip="Back to Profile" />
            <div className="flex items-center gap-4 mb-8">
                <h1 className="text-3xl font-bold">Change Password</h1>
            </div>

            <div className="max-w-2xl">
                <Card>
                    <CardHeader>
                        <CardTitle>Update Your Password</CardTitle>
                        <CardDescription>
                            Choose a strong password to protect your account
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {successMessage && (
                            <Alert className="mb-6 bg-green-50 border-green-200">
                                <AlertDescription className="text-green-800">
                                    {successMessage}
                                </AlertDescription>
                            </Alert>
                        )}

                        {(error || changePasswordMutation.error) && (
                            <Alert variant="destructive" className="mb-6">
                                <AlertDescription>
                                    {error ||
                                        (changePasswordMutation.error instanceof Error
                                            ? changePasswordMutation.error.message
                                            : 'Failed to change password')}
                                </AlertDescription>
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="currentPassword">Current Password</Label>
                                <Input
                                    id="currentPassword"
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    required
                                    disabled={changePasswordMutation.isPending}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="newPassword">New Password</Label>
                                <Input
                                    id="newPassword"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    disabled={changePasswordMutation.isPending}
                                />
                                <PasswordPolicyFeedback
                                    password={newPassword}
                                    state={passwordPolicyState}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    disabled={changePasswordMutation.isPending}
                                />
                            </div>

                            <div className="flex gap-4">
                                <Button
                                    type="submit"
                                    disabled={
                                        changePasswordMutation.isPending ||
                                        !currentPassword ||
                                        !confirmPassword ||
                                        passwordPolicyBlocksSubmit(passwordPolicyState, newPassword)
                                    }
                                >
                                    {changePasswordMutation.isPending
                                        ? 'Changing Password...'
                                        : 'Change Password'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate('/profile')}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default function PasswordPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <PasswordContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
